import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { getIdTokenResult, onAuthStateChanged, type User } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { isRole, type Role } from '@/types/roles';

type AuthValue = {
  user: User | null;
  /**
   * Every role this account holds. A person may hold several — a recruiter who
   * also nominates is one account, not two.
   */
  roles: Role[];
  /** The first of them, for the places that still show a single one. */
  role: Role | null;
  /**
   * The rest of the verified token. `candidateId` lives here, and it is how a
   * candidate's own screens find their record without a lookup by phone.
   */
  claims: Record<string, unknown>;
  /**
   * True while Firebase is still reading the stored session. It is NOT
   * "signed out": a guard that treats this tick as a refusal throws people out
   * of their own pages on every refresh, and makes every route unlinkable.
   */
  restoring: boolean;
  /** Forces a token refresh after a role change, without a re-login. */
  refreshRole: () => Promise<void>;
};

const AuthContext = createContext<AuthValue | null>(null);

export function useAuth(): AuthValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth used outside AuthProvider');
  return ctx;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [role, setRole] = useState<Role | null>(null);
  const [claims, setClaims] = useState<Record<string, unknown>>({});
  const [restoring, setRestoring] = useState(true);

  const readToken = useCallback(async (current: User | null, force = false) => {
    if (!current) return { roles: [] as Role[], role: null as Role | null, claims: {} as Record<string, unknown> };
    const result = await getIdTokenResult(current, force);
    // An unknown or absent claim is a guest. Failing open here would hand a
    // stale or malformed token whatever the UI's default happened to be.
    //
    // `role` is read when `roles` is absent, so a token minted before roles
    // were plural keeps working until its holder next signs in.
    const claimed = Array.isArray(result.claims.roles)
      ? (result.claims.roles as unknown[])
      : (result.claims.role ? [result.claims.role] : []);
    const valid = claimed.filter(isRole) as Role[];
    const roles = valid.length > 0 ? valid : (['guest'] as Role[]);
    return {
      roles,
      role: roles[0],
      claims: result.claims as Record<string, unknown>,
    };
  }, []);

  useEffect(() => {
    return onAuthStateChanged(auth, async (next) => {
      setUser(next);
      const read = await readToken(next);
      setRoles(read.roles);
      setRole(read.role);
      setClaims(read.claims);
      setRestoring(false);
    });
  }, [readToken]);

  const refreshRole = useCallback(async () => {
    const read = await readToken(auth.currentUser, true);
    setRoles(read.roles);
    setRole(read.role);
    setClaims(read.claims);
  }, [readToken]);

  const value = useMemo(
    () => ({ user, roles, role, claims, restoring, refreshRole }),
    [user, roles, role, claims, restoring, refreshRole]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
